import { useState, useEffect } from "react";

// @mui
import Card from "@mui/material/Card";
import Pagination from "@mui/material/Pagination";
import { Grid, Icon, Typography } from "@mui/material";

// Soft UI
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftPagination from "components/SoftPagination";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "./DataTable/index";
import { API_URL } from "config";
import SearchableSelect from "components/SearchableSelect";

// API
import { getDepartments } from "api/departments";
import { getDesignations } from "api/designation";
import {
  getBranches,
  getManagersByBranch,
  getShiftsByBranch,
  getTeamLeadersByBranchAndShift,
} from "api/branches";
import { getRoles } from "api/roles";
import SoftInput from "components/SoftInput";
import FlagLoader from "layouts/flagloader";
import { roleAllowsMultipleShifts } from "utils/accessRole";

/** ---------- helpers ---------- */
const renderWithDelta = (value, delta) => {
  const d = Number(delta || 0);
  let color = "text.secondary";
  let sign = "";
  if (d > 0) {
    color = "success.main";
    sign = "+";
  } else if (d < 0) {
    color = "error.main";
  } else {
    color = "info.main";
  }

  return (
    <SoftBox display="flex" flexDirection="column" alignItems="center">
      <SoftTypography variant="caption" fontWeight="medium" color="text.primary">
        {value}
      </SoftTypography>
      <SoftTypography variant="caption" fontWeight="regular" sx={{ color }}>
        ({sign}
        {d.toFixed(2)}%)
      </SoftTypography>
    </SoftBox>
  );
};

// safe deep read
const at = (obj, path, fallback = undefined) => {
  try {
    const v = path.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
    return v === undefined ? fallback : v;
  } catch {
    return fallback;
  }
};

const STATUS_ORDER = [
  "partial",
  "completed",
  "terminate",
  "quota_full",
  "s_terminate",
];

/** ---------- filter config (exactly as per your meaning) ---------- */
const FILTERS = [
  {
    value: "d24_prev_month",
    label: "24 Hours",
    cfg: {
      leftPath: "current.d24",
      rightPath: "prev.d24",
      deltaPath: "delta_pct.d24",
      leftTotalPath: "totals.current.d24",
      rightTotalPath: "totals.prev.d24",
      totalPctPath: "totals.delta_pct_total.d24_vs_prev_month_same_date",
      title: "Today (00:00 → Now) vs Last-Month Same Date (full day)",
    },
  },
  {
    value: "d7_prev_month",
    label: "1 Week",
    cfg: {
      leftPath: "current.d7",
      rightPath: "prev.d7",
      deltaPath: "delta_pct.d7",
      leftTotalPath: "totals.current.d7",
      rightTotalPath: "totals.prev.d7",
      totalPctPath: "totals.delta_pct_total.d7_vs_prev_month_window",
      title: "Rolling 7 Days vs Last-Month 7-Day Window",
    },
  },
  {
    value: "month_last",
    label: "1 Month",
    cfg: {
      leftPath: "current.month",
      rightPath: "prev.month",
      deltaPath: "delta_pct.month",
      leftTotalPath: "totals.current.month",
      rightTotalPath: "totals.prev.month",
      totalPctPath: "totals.delta_pct_total.month",
      title: "MTD vs Last Full Month",
    },
  },
  {
    value: "prev_day",
    label: "Previous Day",
    cfg: {
      leftPath: "current.d24",
      rightPath: "previous_day_same_time",
      deltaPath: "delta_pct.vs_prev_day_same_time",
      leftTotalPath: "totals.current.d24",
      rightTotalPath: "totals.previous_day_same_time",
      totalPctPath: "totals.delta_pct_total.d24_vs_prev_day_same_time",
      title: "Today (00:00 → Now) vs Yesterday (same time)",
    },
  },
  {
    value: "prev_week",
    label: "Previous Week",
    cfg: {
      leftPath: "current.d7",
      rightPath: "previous_week_rolling",
      deltaPath: "delta_pct.vs_prev_week_rolling",
      leftTotalPath: "totals.current.d7",
      rightTotalPath: "totals.previous_week_rolling",
      totalPctPath: "totals.delta_pct_total.d7_vs_prev_week_rolling",
      title: "Current Rolling 7 Days vs Previous Rolling Week",
    },
  },
];

const findFilter = (value) => FILTERS.find((f) => f.value === value) || FILTERS[0];

function UserStudy() {
  const [filter, setFilter] = useState("d24_prev_month");

  const [rows, setRows] = useState([]);
  const [loadIng, setLoadIng] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 50,
    current_page: 1,
    prev_page: 1,
    last_page: 1,
  });
  const [searchName, setSearchName] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [fromData, setFromData] = useState(0);
  const [toData, setToData] = useState(0);
  const [totalStudies, setTotalStudies] = useState(0);

  // Form data
  const [formData, setFormData] = useState({
    department_id: "",
    designation_id: "",
    role_id: "",
    branch_id: "",
    subbranch_id: "",
    shift_id: null, // single shift id
    shifts: [], // multi shift ids
    manager_id: "",
    team_leader_id: "",
  });

  // Dropdown data
  const [dropdowns, setDropdowns] = useState({
    department: [],
    designation: [],
    branches: [],
    roles: [],
    branchManagers: [],
    branchShifts: [],
    subBranches: [],
    teamLeaders: [],
  });

  const [isMultiShift, setIsMultiShift] = useState(false);

  // Loading states
  const [loading, setLoading] = useState({
    initial: true,
    branch: false,
    teamLeaders: false,
  });

  // Load departments, designations, branches, roles
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [departmentsRes, designationsRes, branchesRes, rolesRes] = await Promise.all([
          getDepartments(1, 250),
          getDesignations(1, 250),
          getBranches(1, 250),
          getRoles(1, 250),
        ]);

        setDropdowns((prev) => ({
          ...prev,
          department: Array.isArray(departmentsRes?.data)
            ? departmentsRes.data.map((d) => ({
              label: d.name,
              value: d.id,
            }))
            : [],
          designation: Array.isArray(designationsRes?.data)
            ? designationsRes.data.map((d) => ({
              label: d.name,
              value: d.id,
            }))
            : [],
          branches: Array.isArray(branchesRes?.data)
            ? branchesRes.data.map((b) => ({
              label: b.name,
              value: b.id,
            }))
            : [],
          roles: Array.isArray(rolesRes?.roles)
            ? rolesRes.roles.map((r) => ({
                label: r.name,
                value: r.id,
                user_scope: r.user_scope,
                scopes: r.scopes || [],
              }))
            : [],
        }));
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading((prev) => ({ ...prev, initial: false }));
      }
    };

    loadInitialData();
  }, []);

  // Load branch managers + shifts
  useEffect(() => {
    const loadBranchData = async () => {
      if (!formData.branch_id) {
        setDropdowns((prev) => ({
          ...prev,
          branchManagers: [],
          branchShifts: [],
          subBranches: [],
          teamLeaders: [],
        }));

        setFormData((prev) => ({
          ...prev,
          manager_id: "",
          team_leader_id: "",
          shifts: [],
          shift_id: null,
          subbranch_id: "",
        }));
        return;
      }

      setLoading((prev) => ({ ...prev, branch: true }));

      try {
        // fetch managers (by branch) and sub-branches; shifts will be loaded after sub-branch selection
        const managersRes = await getManagersByBranch(formData.branch_id);
        const managers = Array.isArray(managersRes?.data)
          ? managersRes.data
          : Array.isArray(managersRes)
            ? managersRes
            : [];

        // fetch sub-branches via API
        const resp = await fetch(`${API_URL}/sub-branches/by-branch/${formData.branch_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        });
        const subData = await resp.json();
        const payload = subData?.data ?? [];
        const subBranches = (Array.isArray(payload) ? payload : []).map((b) => ({ label: b.name, value: b.id }));

        setDropdowns((prev) => ({
          ...prev,
          branchManagers: managers.map((m) => ({
            label: m.name || `${m.first_name || ""} ${m.last_name || ""}`.trim(),
            value: m.id,
          })),
          branchShifts: [],
          subBranches,
        }));
      } catch (error) {
        console.error("Error loading branch data:", error);
      } finally {
        setLoading((prev) => ({ ...prev, branch: false }));
      }
    };

    loadBranchData();
  }, [formData.branch_id]);

  // Load team leaders (branch + shift dependent)
  useEffect(() => {
    const loadTeamLeaders = async () => {
      if (!formData.branch_id || !formData.shift_id) {
        setDropdowns((prev) => ({ ...prev, teamLeaders: [] }));
        setFormData((prev) => ({ ...prev, team_leader_id: "" }));
        return;
      }

      setLoading((prev) => ({ ...prev, teamLeaders: true }));

      try {
        const response = await getTeamLeadersByBranchAndShift(
          formData.branch_id,
          formData.shift_id
        );
        const teamLeaders = response?.team_leaders || [];

        setDropdowns((prev) => ({
          ...prev,
          teamLeaders: teamLeaders.map((tl) => ({
            label: tl.name || "Unknown",
            value: tl.id,
          })),
        }));
      } catch (error) {
        console.error("Error loading team leaders:", error);
        setDropdowns((prev) => ({ ...prev, teamLeaders: [] }));
      } finally {
        setLoading((prev) => ({ ...prev, teamLeaders: false }));
      }
    };

    loadTeamLeaders();
  }, [formData.branch_id, formData.shift_id]);

  // Load shifts when sub-branch changes
  useEffect(() => {
    const loadShiftsBySubbranch = async () => {
      if (!formData.subbranch_id) {
        setDropdowns((prev) => ({ ...prev, branchShifts: [] }));
        setFormData((prev) => ({ ...prev, shifts: [], shift_id: null }));
        return;
      }

      try {
        const resp = await fetch(`${API_URL}/sub-branches/${formData.subbranch_id}/shifts`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        });
        const data = await resp.json();
        if (data && data.success) {
          const shifts = data.shifts || [];
          setDropdowns((prev) => ({
            ...prev,
            branchShifts: shifts.map((s) => ({
              label: `${s.type} (${s.shift_code} ${s.start_time}-${s.end_time})`,
              value: s.id,
            })),
          }));
        } else {
          setDropdowns((prev) => ({ ...prev, branchShifts: [] }));
        }
      } catch (err) {
        console.error("Error fetching shifts by sub-branch:", err);
        setDropdowns((prev) => ({ ...prev, branchShifts: [] }));
      }
    };

    loadShiftsBySubbranch();
  }, [formData.subbranch_id]);

  // Check if role allows multi-shift
  useEffect(() => {
    if (!formData.role_id) {
      setIsMultiShift(false);
      return;
    }

    const role = dropdowns.roles.find((r) => r.value === formData.role_id);

    setIsMultiShift(roleAllowsMultipleShifts(role));

    // reset shift(s) when switching modes
    setFormData((prev) => ({ ...prev, shift_id: null, shifts: [] }));
  }, [formData.role_id, dropdowns.roles]);

  // ---------------- HANDLERS ----------------
  const handleSelectChange = (field) => (option) => {
    setFormData((prev) => ({ ...prev, [field]: option?.value || "" }));
  };

  const handleMultiSelectChange = (field) => (options) => {
    setFormData((prev) => ({
      ...prev,
      [field]: options ? options.map((opt) => opt.value) : [],
    }));
  };

  // Selected role helper
  const selectedRole =
    dropdowns?.roles?.find((r) => r.value === formData.role_id)?.label?.toLowerCase() || "";

  useEffect(() => {
    const fetchStatusCounts = async () => {
      try {
        setLoadIng(true);
        const token = localStorage.getItem("authToken");

        if (!token) throw new Error("Authentication token missing");

        let queryString = `page=${currentPage}&per_page=50&q=${searchName}`;

        if (formData.role_id && formData.role_id !== "null") {
          queryString += `&role_id=${formData.role_id}`;
        }

        if (formData.shift_id && formData.shift_id !== "null") {
          queryString += `&shift_id=${formData.shift_id}`;
        }

        if (formData.shifts && formData.shifts.length > 0) {
          formData.shifts.forEach((id) => {
            queryString += `&shift_id[]=${id}`;
          });
        }
        if (formData.branch_id && formData.branch_id !== "null") {
          queryString += `&branch_id=${formData.branch_id}`;
        }

        if (formData.subbranch_id && formData.subbranch_id !== "null") {
          queryString += `&subbranch_id=${formData.subbranch_id}`;
        }

        if (formData.department_id && formData.department_id !== "null") {
          queryString += `&department_id=${formData.department_id}`;
        }

        if (formData.designation_id && formData.designation_id !== "null") {
          queryString += `&designation_id=${formData.designation_id}`;
        }

        const response = await fetch(`${API_URL}/studies/all-status-counts/user?${queryString}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const result = await response.json();

        if (result.success && result.users) {
          const f = findFilter(filter).cfg;

          const formattedRows = result.users.map((item) => {
            const { user, windows } = item;

            const data = at(windows, f.leftPath, {}) || {};
            const dataprev = at(windows, f.rightPath, {}) || {};
            const delta = at(windows, f.deltaPath, {}) || {};

            const totalCurr = Number(at(windows, f.leftTotalPath, 0) || 0);
            const totalPrev = Number(at(windows, f.rightTotalPath, 0) || 0);
            const totalPct = Number(at(windows, f.totalPctPath, 0) || 0);

            return {
              user: user?.name || "N/A",

              // Total Hits (stacked C/P + %)
              totalHits: renderWithDelta(
                <>
                  <div>C: {totalCurr}</div>
                  <div>P: {totalPrev}</div>
                </>,
                totalPct
              ),

              // status-wise
              partial: renderWithDelta(
                <>
                  <div>C: {data.partial ?? 0}</div>
                  <div>P: {dataprev.partial ?? 0}</div>
                </>,
                delta.partial ?? 0
              ),
              completed: renderWithDelta(
                <>
                  <div>C: {data.completed ?? 0}</div>
                  <div>P: {dataprev.completed ?? 0}</div>
                </>,
                delta.completed ?? 0
              ),
              terminate: renderWithDelta(
                <>
                  <div>C: {data.terminate ?? 0}</div>
                  <div>P: {dataprev.terminate ?? 0}</div>
                </>,
                delta.terminate ?? 0
              ),
              quotaFull: renderWithDelta(
                <>
                  <div>C: {data.quota_full ?? 0}</div>
                  <div>P: {dataprev.quota_full ?? 0}</div>
                </>,
                delta.quota_full ?? 0
              ),
              sTerminate: renderWithDelta(
                <>
                  <div>C: {data.s_terminate ?? 0}</div>
                  <div>P: {dataprev.s_terminate ?? 0}</div>
                </>,
                delta.s_terminate ?? 0
              ),
            };
          });

          console.log(result);
          setRows(formattedRows);
          // setPagination(result.pagination);

          setTotalStudies(result.pagination.total);
          setTotalPages(result.pagination.last_page);
          setFromData(result.pagination.from);
          setToData(result.pagination.to);
        } else {
          setRows([]);
        }
      } catch (error) {
        console.error("Error fetching status counts:", error);
        setRows([]);
      } finally {
        setLoadIng(false);
      }
    };

    fetchStatusCounts();
  }, [filter, currentPage, searchName, formData]);

  /* ---------- HANDLER FUNCTIONS ---------- */
  const handleInputChange = (event) => {
    const value = event.target.value;
    if (value === "") {
      setCurrentPage("");
      return;
    }
    const pageNumber = Number(value);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  // table headers (Total Hits moved to LAST column)
  const tableData = {
    columns: [
      { Header: "User", accessor: "user", align: "left" },
      { Header: "Partial", accessor: "partial", align: "center" },
      { Header: "Completed", accessor: "completed", align: "center" },
      { Header: "Terminate", accessor: "terminate", align: "center" },
      { Header: "Quota Full", accessor: "quotaFull", align: "center" },
      { Header: "S-Terminate", accessor: "sTerminate", align: "center" },
      { Header: "Total Hits", accessor: "totalHits", align: "center" }, // <-- last column
    ],
    rows,
  };

  const activeFilter = findFilter(filter);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        {/* Filters row */}
        <SoftBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md="auto">
              <SoftSelect
                placeholder="Select Filter"
                options={FILTERS.map(({ value, label }) => ({ value, label }))}
                onChange={(opt) => setFilter(opt?.value || FILTERS[0].value)}
                value={{ value: activeFilter.value, label: activeFilter.label }}
                size="small"
                isClearable={true}
              />
            </Grid>

            <Grid item xs={12} md>
              <SoftTypography variant="caption" color="text">
                {activeFilter.cfg.title}
              </SoftTypography>
            </Grid>
          </Grid>
        </SoftBox>

        {/* Data Table */}
        <Card style={{ flex: 1, borderRadius: "12px", padding: "10px" }}>
          {/* Dropdown Fields */}
          <div className="container-fluid">
            <div className="row g-3 align-items-end">
              {/* Role */}
              <div className="col-md">
                <Typography variant="body2" gutterBottom style={{ fontSize: "0.8125rem" }}>
                  Role
                </Typography>

                <SoftSelect
                  className="basic-multi-select"
                  classNamePrefix="select"
                  name="roles"
                  options={dropdowns.roles}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  size="small"
                  fontSize={"0.875rem"}
                  onChange={handleSelectChange("role_id")}
                  value={dropdowns.roles.find((opt) => opt.value === formData.role_id) || null}
                  isClearable={true}
                />
              </div>

              {/* Branch */}
              <div className="col-md">
                <Typography variant="body2" gutterBottom style={{ fontSize: "0.8125rem" }}>
                  Branch
                </Typography>
                <SoftSelect
                  className="basic-multi-select"
                  classNamePrefix="select"
                  name="branch_id"
                  size="small"
                  options={dropdowns.branches}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  fontSize={"0.875rem"}
                  onChange={handleSelectChange("branch_id")}
                  value={dropdowns.branches.find((opt) => opt.value === formData.branch_id) || null}
                  isClearable={true}
                />
              </div>

              {/* Sub Branch */}
              <div className="col-md">
                <Typography variant="body2" gutterBottom style={{ fontSize: "0.8125rem" }}>
                  Sub Branch
                </Typography>
                <SoftSelect
                  className="basic-multi-select"
                  classNamePrefix="select"
                  name="subbranch_id"
                  size="small"
                  options={dropdowns.subBranches}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  fontSize={"0.875rem"}
                  onChange={(opt) =>
                    setFormData((prev) => ({ ...prev, subbranch_id: opt?.value || "", shift_id: null, shifts: [] }))
                  }
                  value={dropdowns.subBranches.find((opt) => opt.value === formData.subbranch_id) || null}
                  isClearable={true}
                />
              </div>

              {/* Shift(s) */}
              <div className="col-md">
                <Typography variant="body2" gutterBottom style={{ fontSize: "0.8125rem" }}>
                  {isMultiShift ? "Shifts (Select Multiple)" : "Shift"}
                </Typography>

                {loading.branch ? (
                  <Typography variant="body2" gutterBottom style={{ fontSize: "0.8125rem" }}>
                    Loading shifts...
                  </Typography>
                ) : isMultiShift ? (
                  <SoftSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    name="shift_id"
                    options={dropdowns.branchShifts}
                    isMulti={true}
                    size="small"
                    closeMenuOnSelect={true}
                    fontSize={"0.875rem"}
                    onChange={handleMultiSelectChange("shifts")}
                    value={dropdowns.branchShifts.filter((opt) =>
                      formData.shifts?.includes(opt.value)
                    )}
                    isClearable={true}
                  />
                ) : (
                  <SoftSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    name="shift_id"
                    options={dropdowns.branchShifts}
                    isMulti={false}
                    closeMenuOnSelect={true}
                    fontSize={"0.875rem"}
                    size="small"
                    onChange={handleSelectChange("shift_id")}
                    value={
                      dropdowns.branchShifts.find((opt) => opt.value === formData.shift_id) || null
                    }
                    isClearable={true}
                  />
                )}
              </div>

              {/* Department */}
              <div className="col-md">
                <Typography variant="body2" gutterBottom style={{ fontSize: "0.8125rem" }}>
                  Department
                </Typography>

                <SoftSelect
                  className="basic-multi-select"
                  classNamePrefix="select"
                  name="department_id"
                  options={dropdowns.department}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  fontSize={"0.875rem"}
                  size="small"
                  onChange={handleSelectChange("department_id")}
                  value={
                    dropdowns.department.find((opt) => opt.value === formData.department_id) || null
                  }
                  isClearable={true}
                />
              </div>

              {/* Designation */}
              <div className="col-md">
                <Typography variant="body2" gutterBottom style={{ fontSize: "0.8125rem" }}>
                  Designation
                </Typography>

                <SoftSelect
                  sx={{ borderRadius: "10px" }}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  name="designation_id"
                  options={dropdowns.designation}
                  isMulti={false}
                  size="small"
                  closeMenuOnSelect={true}
                  fontSize={"0.875rem"}
                  onChange={handleSelectChange("designation_id")}
                  value={
                    dropdowns.designation.find((opt) => opt.value === formData.designation_id) ||
                    null
                  }
                  isClearable={true}
                />
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-md-4 ms-auto">
                <Typography variant="" gutterBottom style={{ fontSize: "0.8125rem" }}>
                  Search
                </Typography>
                <SoftInput
                  variant="outlined"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="Enter name..."
                  sx={{ borderRadius: "10px", minHeight: "38px", width: "12rem", ml: "auto" }}
                />
              </div>
            </div>

            <SoftBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={1}
              style={{ width: "100%", fontSize: "0.875rem" }}
            >
              <SoftBox fontWeight="small" style={{ width: "100%" }}>
                Showing {fromData} to {toData} of {totalStudies} entries
              </SoftBox>

              <SoftBox
                display="flex"
                justifyContent="end"
                alignItems="center"
                p={1}
                style={{ width: "100%" }}
              >
                <SoftPagination item onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                  <Icon sx={{ fontWeight: "bold" }}>first_page</Icon>
                </SoftPagination>

                <SoftPagination item onClick={handlePreviousPage} disabled={currentPage === 1}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </SoftPagination>

                <span
                  style={{
                    margin: "0 5px",
                    width: "120px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span width="5rem" mx={1} style={{ fontSize: "0.8125rem" }}>
                    <SoftInput
                      inputProps={{ type: "number", min: 1, max: totalPages }}
                      value={currentPage}
                      onChange={handleInputChange}
                    />
                  </span>
                  /<span>{totalPages}</span>
                </span>

                <SoftPagination item onClick={handleNextPage} disabled={currentPage === totalPages}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </SoftPagination>

                <SoftPagination
                  item
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <Icon sx={{ fontWeight: "bold" }}>last_page</Icon>
                </SoftPagination>
              </SoftBox>
            </SoftBox>
          </div>

          {loadIng ? (
            // <div className="text-center w-100">
            //   <div
            //     className="spinner-border text-primary"
            //     role="status"
            //     style={{ width: "3rem", height: "3rem" }}
            //   />
            // </div>
            <FlagLoader />
          ) : (
            <DataTable table={tableData} entriesPerPage={false} />
          )}
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UserStudy;
